/**
 * AppLayout.js — Source unique de vérité pour tous les layouts rôle (v3.9)
 *
 * Remplace AdminLayout.js (577 lignes), IntervenantLayout.js (572 lignes),
 * ClientLayout.js (697 lignes) en centralisant tout le scaffold identique.
 *
 * Usage :
 *   <AppLayout navItems={ADMIN_NAV}>                // children pattern (Admin/Client)
 *   <AppLayout navItems={INTERVENANT_NAV} />        // Outlet pattern (Intervenant)
 *
 * navItems : Array<{ to: string, icon: string, label: string, exact?: boolean }>
 */

import React, { useState, useEffect, useCallback, useRef, useLayoutEffect } from 'react';
import styled from 'styled-components';
import { hasBlockingDocumentOverlay } from '../../utils/hasBlockingDocumentOverlay';
import { useAuth } from '../../services/AuthContext';
import { Link, useLocation, useNavigate, Outlet } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import OfflineSyncStatus from '../Common/OfflineSyncStatus';

// ─────────────────────────────────────────────────────────────────────────────
// SCAFFOLD (identique dans les 4 anciens layouts)
// ─────────────────────────────────────────────────────────────────────────────

const LayoutContainer = styled.div`
  min-height: 100vh;
  background: url('/images/background.jpg') center/cover fixed;
  position: relative;
  overflow-x: hidden;
  overflow-x: clip;

  &::before {
    content: '';
    position: fixed;
    top: 0; left: 0; right: 0; bottom: 0;
    background: ${({ theme }) => theme.colors.overlayLight};
    z-index: 1;
  }
`;

const MainContent = styled.div`
  position: relative;
  z-index: 2;
  display: flex;
  min-height: 100vh;
`;

const HamburgerButton = styled.button`
  display: none;
  position: absolute;
  top: 15px;
  left: 10px;
  z-index: 1003;
  background: ${({ theme }) => theme.colors.sidebarBg};
  color: white;
  border: none;
  border-radius: 8px;
  padding: 10px 12px;
  cursor: pointer;
  box-shadow: 0 2px 8px rgba(0,0,0,0.2);
  transition: background 0.3s ease, transform 0.15s ease;
  visibility: hidden;
  opacity: 0;

  @media (min-width: 769px) and (max-width: 1024px) and (orientation: landscape) {
    display: none !important;
  }
  @media (min-width: 769px) and (max-width: 1024px) and (orientation: portrait) {
    display: ${props => props.$open ? 'none' : 'flex'} !important;
    visibility: visible !important;
    opacity: 1 !important;
    align-items: center;
    justify-content: center;
    font-size: 26px;
    min-width: 48px;
    min-height: 48px;
    padding: 12px;
    top: 15px;
    left: 10px;
    touch-action: manipulation;
    -webkit-tap-highlight-color: transparent;
    user-select: none;
    will-change: transform;
    &:hover { background: ${({ theme }) => theme.colors.primaryDark}; transform: scale(1.05); }
    &:active { transform: scale(0.95); background: ${({ theme }) => theme.colors.primaryDark}; }
    &:focus { outline: 2px solid ${({ theme }) => theme.colors.primaryDark}; outline-offset: 2px; }
  }
  @media (max-width: 768px) and (orientation: landscape) {
    display: none !important;
    visibility: hidden !important;
    opacity: 0 !important;
  }
  @media (max-width: 768px) and (orientation: portrait) {
    display: ${props => props.$open ? 'none' : 'flex'} !important;
    visibility: visible !important;
    opacity: 1 !important;
    align-items: center;
    justify-content: center;
    font-size: 26px;
    min-width: 48px;
    min-height: 48px;
    padding: 12px;
    top: 15px;
    left: 10px;
    touch-action: manipulation;
    -webkit-tap-highlight-color: transparent;
    user-select: none;
    will-change: transform;
    &:hover { background: ${({ theme }) => theme.colors.primaryDark}; transform: scale(1.05); }
    &:active { transform: scale(0.95); background: ${({ theme }) => theme.colors.primaryDark}; }
    &:focus { outline: 2px solid ${({ theme }) => theme.colors.primaryDark}; outline-offset: 2px; }
  }
`;

const ContentArea = styled.div`
  flex: 1;
  min-width: 0;
  margin-left: 260px;
  padding: 0;
  transition: margin-left 0.3s ease;
  position: relative;
  max-width: 100%;
  overflow-x: hidden;
  overflow-x: clip;

  @media (min-width: 769px) and (max-width: 1024px) and (orientation: landscape) {
    margin-left: 220px;
  }
  @media (min-width: 769px) and (max-width: 1024px) and (orientation: portrait) {
    margin-left: 0;
    width: 100%;
  }
  @media (max-width: 768px) and (orientation: landscape) {
    margin-left: 200px;
    width: auto;
    max-width: calc(100% - 200px);
  }
  @media (max-width: 768px) and (orientation: portrait) {
    margin-left: 0;
    width: 100%;
  }
`;

const Overlay = styled.div`
  display: none;
  position: fixed;
  top: 0; left: 0; right: 0; bottom: 0;
  background: rgba(0,0,0,0.5);
  z-index: 1001;
  @media (min-width: 769px) and (max-width: 1024px) and (orientation: portrait) {
    display: ${props => props.$isOpen ? 'block' : 'none'};
  }
  @media (max-width: 768px) and (orientation: landscape) {
    display: none;
  }
  @media (max-width: 768px) and (orientation: portrait) {
    display: ${props => props.$isOpen ? 'block' : 'none'};
  }
`;

// ─────────────────────────────────────────────────────────────────────────────
// SIDEBAR (version complète Sidebar.js avec responsive supérieur)
// ─────────────────────────────────────────────────────────────────────────────

const SidebarContainer = styled.div`
  position: fixed;
  left: 0; top: 0;
  height: 100vh;
  height: 100dvh;
  max-height: 100dvh;
  width: 260px;
  background: ${({ theme }) => theme.colors.sidebarBg};
  color: ${({ theme }) => theme.colors.textPrimary};
  z-index: 1000;
  box-shadow: 2px 0 10px rgba(0,0,0,0.1);
  display: flex;
  flex-direction: column;
  transition: transform 0.3s ease-in-out;
  overflow: hidden;

  @media (min-width: 769px) and (max-width: 1024px) and (orientation: landscape) {
    width: 220px;
    transform: translateX(0);
  }
  @media (min-width: 769px) and (max-width: 1024px) and (orientation: portrait) {
    display: none !important;
    width: 260px;
    transform: translateX(-100%);
    &.open { display: flex !important; transform: translateX(0); z-index: 1002; }
  }
  @media (max-width: 768px) and (orientation: landscape) {
    display: flex !important;
    width: 200px;
    transform: translateX(0);
  }
  @media (max-width: 768px) and (orientation: portrait) {
    display: none !important;
    transform: translateX(-100%);
    width: 260px;
    &.open { display: flex !important; transform: translateX(0); z-index: 1002; }
  }
`;

const SidebarHeader = styled.div`
  padding: 1.5rem;
  border-bottom: 1px solid ${({ theme }) => theme.colors.primary};
  text-align: center;
  flex-shrink: 0;
  position: relative;
  @media (min-width: 769px) and (max-width: 1024px) { padding: 1rem 0.5rem; }
  @media (max-width: 768px) { padding: 1rem 0.8rem; }
`;

const Logo = styled.img`
  width: 100px; height: 100px;
  margin-bottom: 0.5rem;
  border-radius: 50%;
  background: ${({ theme }) => theme.colors.white};
  padding: 5px;
  @media (min-width: 769px) and (max-width: 1024px) and (orientation: landscape) { width: 70px; height: 70px; margin-bottom: 0.4rem; }
  @media (min-width: 769px) and (max-width: 1024px) and (orientation: portrait) { width: 60px; height: 60px; margin-bottom: 0.3rem; }
  @media (max-width: 768px) and (orientation: landscape) { width: 50px; height: 50px; margin-bottom: 0.3rem; padding: 3px; }
  @media (max-width: 768px) and (orientation: portrait) { width: 60px; height: 60px; margin-bottom: 0.4rem; }
`;

const AppName = styled.div`
  font-size: 25px;
  font-weight: bold;
  color: white;
  margin-bottom: 0.25rem;
  text-align: center;
  @media (min-width: 769px) and (max-width: 1024px) and (orientation: landscape) { font-size: 18px; line-height: 1.3; }
  @media (min-width: 769px) and (max-width: 1024px) and (orientation: portrait) { font-size: 16px; line-height: 1.3; word-wrap: break-word; overflow-wrap: break-word; hyphens: auto; }
  @media (max-width: 768px) and (orientation: landscape) { font-size: 14px; line-height: 1.3; word-wrap: break-word; overflow-wrap: break-word; hyphens: auto; white-space: normal; }
  @media (max-width: 768px) and (orientation: portrait) { font-size: 12px; line-height: 1.4; word-wrap: break-word; overflow-wrap: break-word; hyphens: auto; padding: 0 2px; white-space: normal; overflow: visible; }
`;

const AppSubtitle = styled.div`
  font-size: 12px;
  opacity: 0.8;
  color: white;
  @media (min-width: 769px) and (max-width: 1024px) { font-size: 10px; }
  @media (max-width: 768px) { font-size: 11px; }
`;

const SidebarMenu = styled.div`
  padding: 1.2rem 1.5rem;
  flex: 1 1 auto;
  overflow-y: auto;
  overflow-x: hidden;
  min-height: 0;
  &::-webkit-scrollbar { width: 6px; }
  &::-webkit-scrollbar-track { background: transparent; }
  &::-webkit-scrollbar-thumb { background: rgba(255,255,255,0.2); border-radius: 3px; &:hover { background: rgba(255,255,255,0.3); } }
  @media (min-width: 769px) and (max-width: 1024px) { padding: 1rem 0.5rem; -webkit-overflow-scrolling: touch; }
  @media (max-width: 768px) { padding: 1rem 0.8rem; -webkit-overflow-scrolling: touch; }
`;

const MenuItem = styled.div`
  margin-bottom: 0.6rem;
  @media (min-width: 769px) and (max-width: 1024px) { margin-bottom: 0.5rem; }
  @media (max-width: 768px) { margin-bottom: 0.6rem; }
`;

/* ✅ 08FEV2026: SCHÉMA LOGIQUE SIDEBAR NAVIGATION V1.0 */
const MenuLink = styled(Link)`
  display: flex;
  align-items: center;
  color: ${({ theme }) => theme.colors.primary};
  text-decoration: none;
  padding: 0.7rem 1rem;
  border-radius: 8px;
  transition: all 0.3s ease;
  white-space: nowrap;
  overflow: hidden;
  font-weight: 500;
  &:hover {
    background: linear-gradient(90deg, ${({ theme }) => theme.colors.primaryDark}, ${({ theme }) => theme.colors.primary});
    color: white;
    transform: translateX(5px);
  }
  &.active {
    background: linear-gradient(90deg, ${({ theme }) => theme.colors.primaryDark}, ${({ theme }) => theme.colors.primary});
    color: white;
    font-weight: 600;
  }
  @media (min-width: 769px) and (max-width: 1024px) {
    padding: 0.7rem 0.6rem;
    min-height: 48px;
    touch-action: manipulation;
    white-space: normal;
    overflow: visible;
    align-items: flex-start;
    > span:not(.menu-icon) { display: inline !important; margin-left: 0.5rem; font-size: 0.85rem; line-height: 1.3; word-wrap: break-word; overflow-wrap: break-word; }
  }
  @media (max-width: 768px) {
    padding: 0.75rem 0.9rem;
    font-size: 0.9rem;
    min-height: 48px;
    touch-action: manipulation;
    -webkit-tap-highlight-color: transparent;
    white-space: normal;
    overflow: visible;
    align-items: flex-start;
  }
`;

const MenuIcon = styled.span`
  margin-right: 0.8rem;
  font-size: 1.2rem;
  flex-shrink: 0;
  @media (min-width: 769px) and (max-width: 1024px) { margin-right: 0.5rem; font-size: 1.3rem; align-self: flex-start; margin-top: 0.1rem; }
  @media (max-width: 768px) { margin-right: 0.7rem; font-size: 1.1rem; align-self: flex-start; margin-top: 0.1rem; }
`;

const SidebarFooter = styled.div`
  padding: 1rem 1.5rem;
  border-top: 1px solid ${({ theme }) => theme.colors.primary};
  flex-shrink: 0;
  margin-top: auto;
  position: sticky;
  bottom: 0;
  background: ${({ theme }) => theme.colors.sidebarBg};
  z-index: 2;
  @media (min-width: 769px) and (max-width: 1024px) { padding: 1rem 0.5rem; }
  @media (max-width: 768px) { padding: 1rem 0.8rem; }
`;

const LogoutButton = styled.button`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0.8rem 0.75rem;
  background: linear-gradient(135deg, ${({ theme }) => theme.colors.error} 0%, rgba(255, 1, 1, 1) 100%);
  color: white;
  border: none;
  border-radius: 8px;
  font-size: 0.95rem;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.3s ease;
  flex-shrink: 0;
  gap: 0.5rem;
  span { white-space: nowrap; }
  &:hover { transform: translateY(-2px); box-shadow: 0 6px 16px rgba(189,1,1,0.4); }
  &:active { transform: translateY(0); }
  @media (min-width: 769px) and (max-width: 1024px) { padding: 0.7rem 0.6rem; font-size: 0.85rem; min-height: 48px; touch-action: manipulation; white-space: normal; }
  @media (max-width: 768px) { padding: 0.75rem 0.6rem; font-size: 0.8rem; line-height: 1.4; touch-action: manipulation; -webkit-tap-highlight-color: transparent; white-space: normal; align-items: center; }
`;

// ─────────────────────────────────────────────────────────────────────────────
// COMPOSANT PRINCIPAL
// ─────────────────────────────────────────────────────────────────────────────

/**
 * @param {Array} navItems  — items de navigation : [{ to, icon, label, exact? }]
 * @param {node}  children  — contenu de la page (pattern Admin/Client)
 *                            Si absent → <Outlet /> (pattern Intervenant)
 */
const AppLayout = ({ navItems = [], children }) => {
  const { t } = useTranslation();
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const hamburgerRef = useRef(null);
  const contentAreaRef = useRef(null);
  const [hasWelcomeHeader, setHasWelcomeHeader] = useState(false);
  const [hasBlockingOverlay, setHasBlockingOverlay] = useState(false);

  /* ── Fermer sidebar sur resize / orientation change vers landscape / desktop ── */
  useEffect(() => {
    const handleResize = () => {
      const isPortrait = window.matchMedia('(orientation: portrait)').matches;
      if (window.innerWidth > 1024 || (!isPortrait && window.innerWidth > 768)) setSidebarOpen(false);
    };
    window.addEventListener('resize', handleResize);
    window.addEventListener('orientationchange', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('orientationchange', handleResize);
    };
  }, []);

  /* ── Fermer sidebar à chaque navigation ── */
  useEffect(() => {
    setSidebarOpen(false);
  }, [location.pathname]); // eslint-disable-line react-hooks/exhaustive-deps

  /* ── Positionnement hamburger en bas de la WelcomeSection ── */
  useLayoutEffect(() => {
    const update = () => {
      const area = contentAreaRef.current;
      if (!area) return;

      // Hide hamburger when a blocking modal/overlay is in front.
      const hasOverlay = hasBlockingDocumentOverlay();
      setHasBlockingOverlay(hasOverlay);

      const welcomeSection = area.querySelector('[data-welcome-header="true"]');
      setHasWelcomeHeader(Boolean(welcomeSection));
      const btn = hamburgerRef.current;
      if (!btn) return;
      const isPortrait = window.matchMedia('(orientation: portrait)').matches;
      const isCompactPortrait = isPortrait && window.innerWidth <= 1024;
      const isCompactLandscapeMobile = !isPortrait && window.innerWidth <= 768;
      if ((!isCompactPortrait && !isCompactLandscapeMobile) || hasOverlay) {
        btn.style.top = '';
        btn.style.left = '';
        return;
      }
      if (!welcomeSection) {
        btn.style.top = '';
        btn.style.left = '';
        return;
      }
      const btnH = btn.offsetHeight || 48;
      const sectionLeft = welcomeSection.offsetLeft || 0;
      btn.style.left = `${Math.max(10, sectionLeft + 10)}px`;
      const sectionBottom = welcomeSection.offsetTop + welcomeSection.offsetHeight;
      btn.style.top = sectionBottom <= (btnH + 20) ? '15px' : `${Math.max(15, sectionBottom - btnH - 10)}px`;
    };

    update();
    const rafId = requestAnimationFrame(update);
    const timerId = setTimeout(update, 150);
    const ro = new ResizeObserver(update);
    const mo = new MutationObserver(() => {
      const welcomeSection = contentAreaRef.current?.querySelector('[data-welcome-header="true"]');
      setHasWelcomeHeader(Boolean(welcomeSection));
      if (welcomeSection) ro.observe(welcomeSection);
      update();
    });
    const area = contentAreaRef.current;
    if (area) {
      ro.observe(area);
      const welcomeSection = area.querySelector('[data-welcome-header="true"]');
      if (welcomeSection) ro.observe(welcomeSection);
      mo.observe(area, { childList: true, subtree: true });
    }

    const bodyObserver = new MutationObserver(update);
    bodyObserver.observe(document.body, { childList: true, subtree: true, attributes: true });

    window.addEventListener('resize', update);
    window.addEventListener('orientationchange', update);
    return () => {
      cancelAnimationFrame(rafId);
      clearTimeout(timerId);
      ro.disconnect();
      mo.disconnect();
      bodyObserver.disconnect();
      window.removeEventListener('resize', update);
      window.removeEventListener('orientationchange', update);
    };
  }, [location.pathname, hasWelcomeHeader]); // eslint-disable-line react-hooks/exhaustive-deps

  const toggleSidebar = useCallback((e) => {
    if (e) { e.preventDefault(); e.stopPropagation(); }
    setSidebarOpen(prev => !prev);
  }, []);

  const handleLogout = useCallback(() => {
    logout();
    navigate('/');
  }, [logout, navigate]);

  const currentPath = typeof location.pathname === 'string'
    ? decodeURIComponent(location.pathname)
    : location.pathname;

  return (
    <LayoutContainer>
      <MainContent>
        {sidebarOpen && <Overlay data-hamburger-ignore="true" $isOpen={sidebarOpen} onClick={toggleSidebar} />}

        <SidebarContainer data-hamburger-ignore="true" className={sidebarOpen ? 'open' : ''}>
          <SidebarHeader>
            <Logo src="/images/logo.apng" alt="Universal Cold Plannit" />
            <AppName>Universal Cold Plannit®</AppName>
            <AppSubtitle>V4.24.94</AppSubtitle>
          </SidebarHeader>

          <SidebarMenu>
            {navItems.map(item => (
              <MenuItem key={item.to}>
                <MenuLink
                  to={item.to}
                  className={
                    item.exact
                      ? currentPath === item.to ? 'active' : ''
                      : currentPath === item.to || currentPath.startsWith(item.to + '/') ? 'active' : ''
                  }
                >
                  <MenuIcon className="menu-icon">{item.icon}</MenuIcon>
                  <span>{item.labelKey ? t(item.labelKey) : item.label}</span>
                </MenuLink>
              </MenuItem>
            ))}
          </SidebarMenu>

          <SidebarFooter>
            <LogoutButton onClick={handleLogout}>
              <span>🚪</span>
              <span>{t('common.signOut')}</span>
            </LogoutButton>
          </SidebarFooter>
        </SidebarContainer>

        <ContentArea ref={contentAreaRef}>
          {hasWelcomeHeader && !hasBlockingOverlay && (
            <HamburgerButton
              ref={hamburgerRef}
              onClick={toggleSidebar}
              $open={sidebarOpen}
              aria-label={t('common.openMenu')}
              type="button"
              aria-expanded={sidebarOpen}
            >
              ☰
            </HamburgerButton>
          )}
          {/* Supporte children (Admin/Client) ou Outlet (Intervenant) */}
          {children ?? <Outlet />}
          <OfflineSyncStatus />
        </ContentArea>
      </MainContent>
    </LayoutContainer>
  );
};

export default AppLayout;
